import DOMPurify from 'isomorphic-dompurify'

/**
 * Shared sanitization for marketplace rich text.
 *
 * Supported schema:
 * - Blocks: p, h2, h3, ul, ol, li, br
 * - Marks: strong, em, u, s, a
 *
 * Everything else is stripped. This keeps listing copy semantic, predictable,
 * and safe across editor, API payloads, persistence, and display.
 */
const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:'])
const DISALLOWED_SCHEME_PATTERN = /^(?:javascript|vbscript|data):/i
const DISALLOWED_URL_CHAR_PATTERN = /\s+/g
const RICH_TEXT_LINK_REL = 'nofollow noopener noreferrer'
const RICH_TEXT_LINK_TARGET = '_blank'

function isBareDomain(value: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9-]{1,63})+.*$/i.test(
    value,
  )
}

export function normalizeRichTextLink(url: string): string | null {
  const trimmed = url.trim()
  if (!trimmed) return null

  const normalizedForSchemeCheck = trimmed.replace(
    DISALLOWED_URL_CHAR_PATTERN,
    '',
  )
  if (DISALLOWED_SCHEME_PATTERN.test(normalizedForSchemeCheck)) {
    return null
  }

  const candidate =
    /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || !isBareDomain(trimmed)
      ? trimmed
      : `https://${trimmed}`

  try {
    const parsed = new URL(candidate)
    if (!ALLOWED_LINK_PROTOCOLS.has(parsed.protocol.toLowerCase())) {
      return null
    }
    if (!parsed.hostname) {
      return null
    }
    return parsed.toString()
  } catch {
    return null
  }
}

const globalSanitizeScope = globalThis as typeof globalThis & {
  __classifiedListingRichTextHooksInitialized?: boolean
}

let hooksInitialized =
  globalSanitizeScope.__classifiedListingRichTextHooksInitialized === true

function initializeDomPurifyHooks() {
  if (hooksInitialized) return

  hooksInitialized = true
  globalSanitizeScope.__classifiedListingRichTextHooksInitialized = true

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const attrName = (data.attrName || '').toLowerCase()
    if (!attrName) return

    if (attrName === 'href') {
      const normalizedHref = normalizeRichTextLink(String(data.attrValue || ''))
      if (!normalizedHref) {
        data.keepAttr = false
        return
      }
      data.attrValue = normalizedHref
      return
    }

    if (attrName === 'target' || attrName === 'rel') {
      data.keepAttr = false
    }

    // Strip every other attribute by default.
    if (attrName !== 'href') {
      data.keepAttr = false
    }

    // Links are the only element with supported attributes.
    if (
      String(node.nodeName || '').toLowerCase() !== 'a' &&
      attrName === 'href'
    ) {
      data.keepAttr = false
    }
  })

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const elementNode = node as {
      nodeType?: number
      tagName?: string
      getAttribute?: (name: string) => string | null
      setAttribute?: (name: string, value: string) => void
      removeAttribute?: (name: string) => void
    }

    if (
      elementNode.nodeType !== 1 ||
      typeof elementNode.getAttribute !== 'function' ||
      typeof elementNode.setAttribute !== 'function' ||
      typeof elementNode.removeAttribute !== 'function'
    ) {
      return
    }

    if (String(elementNode.tagName || '').toLowerCase() !== 'a') {
      elementNode.removeAttribute('style')
      elementNode.removeAttribute('class')
      return
    }

    const href = elementNode.getAttribute('href')
    if (!href) {
      elementNode.removeAttribute('target')
      elementNode.removeAttribute('rel')
      return
    }

    elementNode.setAttribute('target', RICH_TEXT_LINK_TARGET)
    elementNode.setAttribute('rel', RICH_TEXT_LINK_REL)
  })
}

initializeDomPurifyHooks()

export const SANITIZE_CONFIG = {
  RETURN_TRUSTED_TYPE: false as const,
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'h2',
    'h3',
    'ul',
    'ol',
    'li',
    'a',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
}

export function sanitizeHTML(dirty: string): string {
  return String(DOMPurify.sanitize(dirty, SANITIZE_CONFIG))
}
